import type {
  BossExtrudeSessionSelection,
  ExtrudeFeatureEditDefinition as ExtrudePropertyDefinition,
} from '@puppycad/cad-core';
import { ExtrudeFeatureEditDefinitionSanitizer } from '@puppycad/cad-core';
import type { ExtrudeTarget } from './extrude-types';

/**
 * 拉伸预览请求快照。
 */
export interface ExtrudePreviewRequest {
  /** 请求版本号。 */
  version: number;
  /** 选择草稿。 */
  selection: BossExtrudeSessionSelection;
  /** UI 草稿定义。 */
  definition: ExtrudePropertyDefinition;
}

/**
 * 深拷贝拉伸属性定义。
 * @param definition 原始属性定义。
 * @returns 克隆结果。
 */
function cloneExtrudePropertyDefinition(
  definition: ExtrudePropertyDefinition,
): ExtrudePropertyDefinition {
  return ExtrudeFeatureEditDefinitionSanitizer.sanitize(definition);
}

/**
 * 深拷贝拉伸选择草稿。
 * @param selection 选择草稿。
 * @returns 克隆结果。
 */
function cloneSelection(selection: BossExtrudeSessionSelection): BossExtrudeSessionSelection {
  if (selection.kind === 'contours') {
    return {
      kind: 'contours',
      contourIds: [...selection.contourIds],
      contours: selection.contours?.map((contour) => ({
        regionId: contour.regionId,
        geometry: structuredClone(contour.geometry),
        sketchId: contour.sketchId,
        contourSourceObjectId: contour.contourSourceObjectId,
        contourKey: contour.contourKey,
        regionIdentity: contour.regionIdentity
          ? structuredClone(contour.regionIdentity)
          : undefined,
      })),
    };
  }
  return {
    kind: 'face',
    objectId: selection.objectId,
    faceIndex: selection.faceIndex,
    sourceType: selection.sourceType,
    normalHint: selection.normalHint ? [...selection.normalHint] as [number, number, number] : undefined,
  };
}

/**
 * 构建拉伸预览请求。
 * @param input 请求输入。
 * @returns 不可变预览请求快照。
 */
export function buildExtrudePreviewRequest(input: {
  version: number;
  target: ExtrudeTarget;
  definition: ExtrudePropertyDefinition;
}): ExtrudePreviewRequest {
  return {
    version: input.version,
    selection: cloneSelection(input.target.selection),
    definition: cloneExtrudePropertyDefinition(input.definition),
  };
}
